import React, { useEffect, useCallback } from "react";
import { CellMeta, PageData, PageDataCode, PageDataData, update } from "./useFinancialData";
import "./DashboardFinancialTable.scss";
import cx from "classnames";
import { formatNumber } from "./utils";
// @ts-ignore
import { RIEInput } from "riek";
import cropr from "../../shared/utils/cropr";
import gettext from "../../shared/utils/gettext";
import { BalanceAggregateType } from "./DashboardFinancial";
import { Popover } from "react-bootstrap";
import InlineRecordTable from "./InlineRecordTable";
import { CropfieldLinkCell } from "./CropFieldsLinkCell";
import { PopoverCell } from "./PopoverCell";
import useRevenueDefinition from "./useRevenueDefinition";

interface CellContext {
  data: PageDataData;
  cellStyle?: (colnum: number) => string | undefined;
}

const numRegex = /^\d[\d:,.]*$/;
function align(o: any) {
  return typeof o === "number" || (o && numRegex.test(o)) ? "text-right" : "";
}

const Header = ({ data, cellStyle }: CellContext) => {
  if (!(data.header && data.rows.length)) return null;
  return (
    <tr className={"bg-smoke border-smoke"}>
      {data.header.map((e, i) => (
        <th key={i} className={cellStyle?.(i)}>
          {e}
        </th>
      ))}
    </tr>
  );
};

const Summary = ({ data }: CellContext) => {
  if (!(data.summary && data.rows.length && data.summary[data.summary.length - 1])) return null;
  return (
    <tr>
      {data.summary.map((e, i) => (
        <td className={cx(i === 0 && "italic", align(e), "no-border")} key={i}>
          {formatNumber(e, i > 4)}
        </td>
      ))}
    </tr>
  );
};

const changePrice = (p: any) => {
  let form = new FormData();
  for (const [key, value] of Object.entries(p))
    form.append(key, String(value).replace(/[€ ]/g, "").replace(",", ".") || "0");
  return fetch("/cropr/settings/financial_item/", {
    method: "POST",
    credentials: "same-origin",
    body: form,
    headers: { "X-CSRFToken": cropr.getCookie("csrftoken") },
  })
    .then((response) => (response.ok ? update() : cropr.notify("danger", gettext("Failure"))))
    .catch((e) => cropr.notify("danger", gettext("Failure")));
};
const changeYieldPrice = (p: any) => {
  // The RevenueDefinitions are not included in the json-response, ugly way to make it work
  changePrice(p).then(() =>
    cropr.revenueDefinitions.fetch({ data: `plan=${cropr.getActivePlan().id}&limit=1000` })
  );
};
const LinkIt = ({ value, meta }: { value: any; meta: CellMeta }) =>
  meta.link === "cropfields" ? (
    <CropfieldLinkCell cropfields={meta.ids} index={meta.index} value={value} />
  ) : (
    <a href={`#financial/${meta.link}/`}>{value}</a>
  );

interface CellProperties {
  i: number;
  value: any;
  className?: string;
  meta?: CellMeta;
}
type CellRenderer = ({ i, value, className, meta }: CellProperties) => React.ReactElement;
const TotalCell: CellRenderer = ({ i, value, className, meta }: CellProperties) => (
  <td
    className={cx(className, {
      "bg-green--10 border-green--100": i >= 4,
      "text-right": [3, 5, 6].includes(i),
    })}
    colSpan={[1].includes(i) ? 2 : 1}
  >
    {meta?.link ? <LinkIt meta={meta} value={value} /> : i >= 4 ? formatNumber(value, true) : value}
  </td>
);
const SummaryCell: CellRenderer = ({ i, value }: CellProperties) => (
  <td className={cx({ "text-right": i > 0, "bg-yellow-lighter": [2, 3, 4].includes(i) })}>
    {formatNumber(value, i > 0, i === 1, 0)}
  </td>
);
const PriceCell: CellRenderer = ({ i, value }: CellProperties) => (
  <td className={cx({ "text-right": i > 0 })}>{formatNumber(value, i > 0)}</td>
);

interface RecordEditCellProperties {
  value: any;
  filter: any;
}
const InlineRecordEditCell = ({ value, filter }: RecordEditCellProperties) => (
  <PopoverCell value={value}>
    {(props) => (
      <Popover id="recpopover" {...props} className="inline-record-edit-popover">
        <Popover.Title>
          {filter.stock && cropr.stock.get(filter.stock).get("name")}{" "}
          {filter.theme && cropr.theme[filter.theme]?.name}{" "}
          {filter.method && cropr.detailtype[filter.method]?.name}{" "}
          {filter.applier && cropr.farm_contacts.get(filter.applier)?.get("name")}
        </Popover.Title>
        <Popover.Content className="overflow-auto">
          <InlineRecordTable filter={filter} />
        </Popover.Content>
      </Popover>
    )}
  </PopoverCell>
);

const ToHarvestBody = ({ filter }: { filter: any }) => {
  const [revenues] = useRevenueDefinition();
  const costPerKey: any = {};
  for (const r of revenues) {
    costPerKey[r.crop + (r.variety ? "_" + r.variety : "")] = r;
  }
  const results: any = {};
  for (const e of filter.cropfields) {
    const c = cropr.cropfields.get(e);
    const key = c.get("crop") + (c.get("variety") ? "_" + c.get("variety") : "");
    if (results[key]) continue;
    const cost = costPerKey[key] || costPerKey["" + c.get("crop")];
    results[key] = {
      crop: c.getRelated("crop", "name"),
      variety: c.getRelated("variety", "name"),
      cost,
      key,
      secondary: c.get("variety") !== cost?.variety,
    };
  }
  return (
    <>
      {Object.values(results).map((r: any, index) => (
        <tr key={index}>
          <td>{r.crop}</td>
          <td>{r.variety}</td>
          <td className="text-right">
            <RIEInput
              value={r.cost?.["default_yield_per_area_1"] || "-"}
              change={changeYieldPrice}
              propName={`y${r.key}_default`}
              className={`editable${r.secondary ? " secondary" : ""}`}
            />
            &nbsp; ton/{cropr.l10n.unit_area_display}
          </td>
          <td className="text-right">
            <RIEInput
              value={formatNumber(r.cost?.["revenue_per_unit_1"] || 0, true)}
              change={changeYieldPrice}
              propName={`y${r.key}_revenue`}
              className={`editable${r.secondary ? " secondary" : ""}`}
            />
            &nbsp; /ton
          </td>
        </tr>
      ))}
    </>
  );
};

const ToHarvestCell = ({ value, filter }: RecordEditCellProperties) => (
  <PopoverCell value={value}>
    {(props) => (
      <Popover id="recpopover" {...props} className="inline-record-edit-popover">
        <Popover.Title>{gettext("To harvest")}</Popover.Title>
        <Popover.Content className="overflow-auto">
          {gettext(
            "Unharvested area valuation is calculated with default values per crop and variety."
          )}
          <table className="inline-record-edit-overlay table table-striped">
            <thead>
              <tr>
                <th>{gettext("Crop")}</th>
                <th>{gettext("Variety")}</th>
                <th colSpan={2}>{gettext("Default yield")}</th>
              </tr>
            </thead>
            <tbody>
              <ToHarvestBody filter={filter} />
            </tbody>
          </table>
        </Popover.Content>
      </Popover>
    )}
  </PopoverCell>
);

const Cell = ({ i, value, className, meta }: CellProperties) => {
  let val = i === 1 ? value : formatNumber(value, i > 4);
  if (meta) {
    if (meta.price && val !== null) {
      val = (
        <RIEInput value={val} change={changePrice} propName={meta.price} className="editable" />
      );
    } else if (meta.record) {
      const Component = meta.record.batch ? ToHarvestCell : InlineRecordEditCell;
      val = <Component value={val} filter={meta.record} />;
    }
  }
  const c = cx(
    className,
    align(value),
    meta && "text-right",
    i >= 6 && "bg-green--10 border-green--100"
  );
  return <td className={c}>{val}</td>;
};

function cMeta(data: PageDataData, i: number, colNum: number, cropfields?: number[]) {
  const rowMeta = data.meta?.[i];
  if (!rowMeta) return;
  if (rowMeta[colNum]) return rowMeta[colNum];
  if (colNum === 1 && cropfields && rowMeta[5]) {
    const price = rowMeta[5].price;
    if (price && /^[smuiwe]/.test(price)) {
      const id = parseInt(price.split("_")[0].substring(1));
      switch (price.charAt(0)) {
        case "s":
          return cropr.stock.get(id)?.get("variant") === "s"
            ? { record: { cropfields, theme: cropr.data.THEME.PLANTING_SEEDING } }
            : { record: { cropfields, stock: id } };
        case "m":
          return { record: { cropfields, method: id } };
        case "u":
          return { record: { cropfields, applier: id, attributes: { work_cost: false } } };
        case "w":
          return { record: { cropfields, applier: id, attributes: { work_cost: true } } };
        case "i":
          return { record: { cropfields, theme: cropr.data.THEME.IRRIGATION } };
        case "e":
          return { record: { cropfields, attributes: { used_fuel: true } } };
      }
    }
    if (rowMeta[5].revenue) {
      return {
        record: {
          cropfields,
          theme: cropr.data.THEME.HARVESTING,
          batch: rowMeta[5].revenue === "TOHARVEST",
        },
      };
    }
  }
}

const Section = ({
  data,
  CellComponent,
  trStyle,
  onClick,
  headerStyle,
  cropfields,
}: {
  data: PageDataData;
  CellComponent: CellRenderer;
  onClick?: (i: number) => void;
  trStyle?: (data: PageDataData, i: number) => string | undefined;
  headerStyle?: (colnum: number) => string | undefined;
  cropfields?: number[];
}) =>
  data.rows.length ? (
    <>
      <Header data={data} cellStyle={headerStyle} />
      {data.rows.map((row, i) => (
        <tr key={i} className={trStyle?.(data, i)} onClick={onClick && (() => onClick(i))}>
          {row.map((e, colNum) => (
            <CellComponent
              key={colNum}
              value={e}
              i={colNum}
              meta={cMeta(data, i, colNum, cropfields)}
            />
          ))}
        </tr>
      ))}
      <Summary data={data} />
      {data.header && data.rows.length > 0 && (
        <tr key="footer">
          <td colSpan={8} />
        </tr>
      )}
    </>
  ) : null;

const trSummary = (data: PageDataData, i: number) =>
  cx({ "bg-yellow-lighter": i === data.rows.length - 1 }, "summary");
const hdrStyle = (start: number) => (i: number) =>
  (i >= start && "bg-green--100 border-green--100") || undefined;
const pageCells: Record<string, CellRenderer> = { summary: SummaryCell, pricelist: PriceCell };
const headerStyles: Record<string, (i: number) => string | undefined> = {
  Total: hdrStyle(5),
  base: hdrStyle(6),
};

// function to log visit to this page to Kibana (since it's /#financial/, it's not in the weblogs)
const sendMetric = (isSummary: boolean, aggregate?: BalanceAggregateType) => {
  const type = ["crop", "field", "variety"][aggregate || 0];
  cropr.metric("financial", type + (isSummary ? "" : " subitem"));
};

function click(aggregate: number, i: number) {
  window.location.hash = `financial/${aggregate}/${i}/`;
}

interface SummaryTableProps {
  page: PageData;
  onClick?: (i: number) => void;
  defaults: any;
}
const SummaryTable = ({ page, onClick, defaults }: SummaryTableProps) => (
  <table className="table table-sm table-light table-bordered dashboard-financial-table">
    <tbody>
      {page.data.map(({ data, code }, i) => (
        <Section
          key={i}
          data={data}
          CellComponent={pageCells[page.name] || (code === "Total" ? TotalCell : Cell)}
          headerStyle={undefined}
          onClick={onClick}
          {...defaults}
        />
      ))}
    </tbody>
  </table>
);

const categories: Record<string, string> = {
  Total: "total",
  harvestedrevenue: "revenue",
  fieldrevenues: "revenue",
};

interface DashboardFinancialTableProps {
  page: PageData;
  aggregate?: BalanceAggregateType;
}
const DashboardFinancialTable = ({ page, aggregate }: DashboardFinancialTableProps) => {
  const pageName = page.name;
  const isSummary = pageName === "summary";
  const defaults = {
    pageName,
    trStyle: isSummary ? trSummary : undefined,
    cropfields: page.cropfields,
  };
  const onClick = useCallback(
    () => (isSummary ? (i: number) => click(aggregate || 0, i + 1) : undefined),
    [aggregate, isSummary]
  );
  useEffect(() => sendMetric(isSummary, aggregate), [isSummary, aggregate]);
  // summary and detail page are rendered differently.
  if (isSummary) return <SummaryTable page={page} onClick={onClick} defaults={defaults} />;
  const pagesPerCategory = page.data.reduce((m, p) => {
    const key: string = categories[p.code] || "cost";
    (m[key] = m[key] || []).push(p);
    return m;
  }, {} as Record<string, PageDataCode[]>);
  return (
    <table className="table table-sm table-light table-bordered dashboard-financial-table">
      <tbody>
        <Section
          data={pagesPerCategory.total[0].data}
          CellComponent={pageCells[pageName] || TotalCell}
          headerStyle={headerStyles[pagesPerCategory.total[0]?.code] || headerStyles.base}
          {...defaults}
        />
        <tr>
          <th colSpan={10} className="heading">
            {gettext("Revenue")}
          </th>
        </tr>
        {pagesPerCategory.revenue?.map(({ data, code }, i) => (
          <Section
            key={`revenue${i}`}
            data={data}
            CellComponent={pageCells[pageName] || Cell}
            headerStyle={headerStyles[code] || headerStyles.base}
            {...defaults}
          />
        ))}
        <tr>
          <th colSpan={10} className="heading">
            {gettext("Costs")}
          </th>
        </tr>
        {pagesPerCategory.cost?.map(({ data, code }, i) => (
          <Section
            key={`cost${i}`}
            data={data}
            CellComponent={pageCells[pageName] || Cell}
            headerStyle={isSummary ? undefined : headerStyles[code] || headerStyles.base}
            {...defaults}
          />
        ))}
      </tbody>
    </table>
  );
};
export default DashboardFinancialTable;
